/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { MapContainer, TileLayer, ScaleControl, LayersControl, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import { LatLngLiteral } from 'leaflet';
import ImageOverlayRotated from './ImageOverlayRotated';
import { useEventContext } from 'providers/EventProvider';
import Players from 'components/Players/Players';
import { useSplitContext } from 'providers/SplitProvider';
import { calculatePlayers } from 'utils/trackHelpers';
import HideAttributeControl from './HideAttributeControl';
import RightDrawer from './RightDrawer';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import 'leafletRotate';

const lerpShortestAngle = (from: number, to: number, t: number) => {
  const delta = ((to - from + 540) % 360) - 180;
  return (from + delta * t + 360) % 360;
};

const FIT_PADDING_PX = 96;

const applyMapView = (
  map: ReturnType<typeof useMap>,
  lat: number | undefined,
  lng: number | undefined,
  bearing: number,
  zoom: number
) => {
  if (lat != null && lng != null) {
    map.setView([lat, lng], zoom, { animate: false });
  }
  map.setBearing(bearing);
  if (lat != null && lng != null) {
    map.setView([lat, lng], zoom, { animate: false });
  }
};

const getZoomToFitPoints = (map: ReturnType<typeof useMap>, a: LatLngLiteral, b: LatLngLiteral) => {
  const size = map.getSize();
  const minDim = Math.min(size.x, size.y) - FIT_PADDING_PX * 2;
  const p1 = map.project([a.lat, a.lng], 0);
  const p2 = map.project([b.lat, b.lng], 0);
  const dist0 = p1.distanceTo(p2);
  if (minDim <= 0 || dist0 < 1e-9) {
    return map.getZoom();
  }
  const zoom = Math.log2(minDim / dist0);
  return Math.max(map.getMinZoom(), Math.min(map.getMaxZoom(), zoom));
};

const SetCenterComponent = ({
  center,
  bearing,
  transitionMs,
  fitPoints,
  autoZoom
}: {
  center: LatLngLiteral | null;
  bearing: number;
  transitionMs: number;
  fitPoints: [LatLngLiteral, LatLngLiteral] | null;
  autoZoom: boolean;
}) => {
  const map = useMap();
  const lat = center?.lat;
  const lng = center?.lng;
  const fitALat = fitPoints?.[0].lat;
  const fitALng = fitPoints?.[0].lng;
  const fitBLat = fitPoints?.[1].lat;
  const fitBLng = fitPoints?.[1].lng;

  React.useEffect(() => {
    map.stop();
    const startZoom = map.getZoom();
    const targetZoom =
      autoZoom && fitALat != null && fitALng != null && fitBLat != null && fitBLng != null
        ? getZoomToFitPoints(map, { lat: fitALat, lng: fitALng }, { lat: fitBLat, lng: fitBLng })
        : startZoom;

    if (!transitionMs || lat == null || lng == null) {
      applyMapView(map, lat, lng, bearing, targetZoom);
      return;
    }

    const startCenter = map.getCenter();
    const startBearing = map.getBearing();
    const startedAt = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - startedAt) / transitionMs);
      const ease = 1 - (1 - t) * (1 - t);
      applyMapView(
        map,
        startCenter.lat + (lat - startCenter.lat) * ease,
        startCenter.lng + (lng - startCenter.lng) * ease,
        lerpShortestAngle(startBearing, bearing, ease),
        startZoom + (targetZoom - startZoom) * ease
      );
      if (t < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [lat, lng, bearing, transitionMs, map, autoZoom, fitALat, fitALng, fitBLat, fitBLng]);
  return null;
};

const ResizeMap = () => {
  const map = useMap();
  React.useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
    });
    observer.observe(container);
    map.invalidateSize();
    return () => observer.disconnect();
  }, [map]);
  return null;
};

type MapEventProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  hiddenRouteIds: number[];
};

const MapEvent = ({ open, setOpen, hiddenRouteIds }: MapEventProps) => {
  const { event, showControls, updatePlayers } = useEventContext();
  const { controls, setControls, mapCenter, mapBearing, mapTransitionMs, mapFitPoints, options } = useSplitContext();

  const updatePosition = (controlId: number) => (e: any) => {
    const position = e.target.getLatLng();
    setControls((controls) => controls.map((c) => (c.id === controlId ? { ...c, position } : c)));
  };

  React.useEffect(() => {
    updatePlayers((players) => [...calculatePlayers(players, controls)]);
  }, [controls]);

  const visibleRoutes = (event.routes || []).filter((route) => !hiddenRouteIds.includes(route.id));

  return (
    <Box className="map" sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        className="sidebar-map"
        center={event.location}
        zoom={event.zoom}
        style={{
          width: '100%',
          height: '100%',
          zIndex: '0',
          cursor: 'default'
        }}
        maxZoom={22}
        rotate
        rotateControl={false}
        shiftKeyRotate={false}
        touchRotate={false}
      >
        <HideAttributeControl />
        <ResizeMap />
        <SetCenterComponent
          center={mapCenter}
          bearing={mapBearing}
          transitionMs={mapTransitionMs}
          fitPoints={mapFitPoints}
          autoZoom={options.autoZoom}
        />
        <TileLayer url="https://tile.thunderforest.com/cycle/{z}/{x}/{y}.png?apikey=8415e76f241042fb85d6e744d874ab6c" />
        <ScaleControl position="bottomleft" imperial={false} />
        <LayersControl position="topleft">
          {event.maps.map((m) => (
            <LayersControl.Overlay key={`map-${m.url || m.id}`} name={m.name} checked={true}>
              <ImageOverlayRotated
                topLeft={m.topLeft}
                topRight={m.topRight}
                bottomLeft={m.bottomLeft}
                opacity={1}
                image={import.meta.env.VITE_UPLOADS + m.url}
              />
            </LayersControl.Overlay>
          ))}
          {visibleRoutes.map((r) => (
            <LayersControl.Overlay key={r.id} name={r.name} checked={true}>
              <Polyline color={r.color} positions={r.track} opacity={0.7} />
            </LayersControl.Overlay>
          ))}
          {showControls &&
            controls.map((c) => (
              <LayersControl.Overlay key={c.id} name={c.name} checked={true}>
                <Marker position={c.position} draggable={true} eventHandlers={{ dragend: updatePosition(c.id) }}>
                  <Popup>{c.name}</Popup>
                </Marker>
              </LayersControl.Overlay>
            ))}
        </LayersControl>
        <Players />
      </MapContainer>
      <IconButton
        color="primary"
        size="large"
        aria-label="Open competitors"
        edge="end"
        onClick={() => setOpen(true)}
        sx={{
          position: 'absolute',
          background: '#ffffffaa',
          zIndex: 1100,
          top: 'env(safe-area-inset-top, 0px)',
          right: 0,
          margin: 0,
          minWidth: 44,
          minHeight: 44,
          display: open ? 'none' : 'flex'
        }}
      >
        <MenuIcon />
      </IconButton>
      <RightDrawer open={open} setOpen={setOpen} />
    </Box>
  );
};

export default MapEvent;
